<template>
  <section class="settlement-list">
    <b-card no-body class="settlement-card mb-2">
      <b-card-body>
        <div class="d-flex flex-wrap align-items-center justify-content-between mb-1">
          <div>
            <h4 class="mb-0">Settlement List</h4>
            <small class="text-muted">Review settlement batches and payment status</small>
          </div>
          <div class="d-flex flex-wrap align-items-center settlement-actions">
            <b-form-input
              v-model="searchTerm"
              class="settlement-search mr-1 mb-50"
              placeholder="Search by bank Tx ID or date..."
            />
            <b-button
              variant="primary"
              class="mb-50"
              :disabled="isLoading || !!dateError"
              @click="settlementListData"
            >
              Refresh
            </b-button>
          </div>
        </div>

        <b-row class="mb-1">
          <b-col
            v-for="item in summaryCards"
            :key="item.label"
            xl="3"
            sm="6"
            class="mb-50"
          >
            <div class="d-flex align-items-center settlement-kpi-card">
              <b-avatar size="36" :variant="item.color" class="mr-1">
                <feather-icon :icon="item.icon" size="18" />
              </b-avatar>
              <div>
                <div class="font-weight-bolder">{{ item.value }}</div>
                <small class="text-muted">{{ item.label }}</small>
              </div>
            </div>
          </b-col>
        </b-row>

        <div class="settlement-filter-panel">
          <b-row>
            <b-col md="3">
              <b-form-group label="From">
                <flat-pickr
                  v-model="from_date"
                  :config="dateConfig"
                  class="form-control"
                  placeholder="YYYY-MM-DD"
                />
              </b-form-group>
            </b-col>
            <b-col md="3">
              <b-form-group label="To">
                <flat-pickr
                  v-model="to_date"
                  :config="dateConfig"
                  class="form-control"
                  placeholder="YYYY-MM-DD"
                />
              </b-form-group>
            </b-col>
            <b-col md="3">
              <b-form-group label="Status">
                <b-form-select
                  v-model="statusFilter"
                  :options="statusFilterOptions"
                  class="custom-select"
                />
              </b-form-group>
            </b-col>
            <b-col md="3">
              <b-form-group label=" ">
                <b-button
                  variant="outline-secondary"
                  class="w-100"
                  :disabled="isLoading"
                  @click="resetFilters"
                >
                  Reset Filters
                </b-button>
              </b-form-group>
            </b-col>
          </b-row>
          <small v-if="dateError" class="text-danger d-block mb-50">
            {{ dateError }}
          </small>
          <div class="d-flex align-items-center justify-content-between quick-range-row">
            <div class="d-flex align-items-center quick-range-list">
              <small class="text-muted mr-50">Quick range:</small>
              <b-badge
                v-for="preset in presetOptions"
                :key="preset.key"
                variant="light-primary"
                :class="['mr-50', 'cursor-pointer', 'settlement-preset', { 'settlement-preset--active': activePreset === preset.key }]"
                @click="applyPreset(preset.key)"
              >
                {{ preset.label }}
              </b-badge>
            </div>
          </div>
        </div>
      </b-card-body>
    </b-card>

    <b-card no-body>
      <b-card-body>
        <div v-if="isLoading" class="settlement-skeleton">
          <div v-for="n in 6" :key="`settlement-skeleton-${n}`" class="settlement-skeleton-row">
            <div class="settlement-skeleton-block settlement-skeleton-block--lg"></div>
            <div class="settlement-skeleton-block"></div>
            <div class="settlement-skeleton-block"></div>
            <div class="settlement-skeleton-block"></div>
            <div class="settlement-skeleton-block settlement-skeleton-block--sm"></div>
          </div>
        </div>
        <b-alert v-else-if="loadError" variant="danger" show>
          {{ loadError }}
        </b-alert>
        <div v-else>
          <div class="d-block d-lg-none">
            <div
              v-for="settlement in filteredRows"
              :key="`settlement-card-${settlement.id || settlement.settlement_from}`"
              class="settlement-card-item mb-1"
            >
              <div class="d-flex align-items-center justify-content-between mb-25">
                <div>
                  <div class="font-weight-bolder">
                    {{ formatDateRange(settlement.settlement_from, settlement.settlement_to) }}
                  </div>
                  <small class="text-muted">Bank Tx ID: {{ settlement.bank_trans_id || "-" }}</small>
                </div>
                <b-badge :variant="statusVariant(settlement.status)">
                  {{ statusLabel(settlement.status) }}
                </b-badge>
              </div>
              <div class="d-flex align-items-center justify-content-between">
                <div>
                  <small class="text-muted">Transactions</small>
                  <div class="font-weight-bolder">{{ settlement.no_of_transaction || 0 }}</div>
                </div>
                <div class="text-right">
                  <small class="text-muted">Received</small>
                  <div class="font-weight-bolder text-dark">
                    {{ formatAmount(settlement.receivable_amount) }}
                  </div>
                </div>
              </div>
              <div class="mt-50 text-right">
                <router-link
                  v-if="settlement.id"
                  :to="{ name: 'admin-settlement-details', params: { settlementId: settlement.id } }"
                  target="_blank"
                  class="font-weight-bold"
                >
                  View details
                </router-link>
              </div>
            </div>
          </div>

          <div class="d-none d-lg-block">
            <vue-good-table
              :columns="columns"
              :rows="filteredRows"
              :line-numbers="true"
              :rtl="direction"
              style-class="vgt-table condensed settlement-table"
              :pagination-options="{
                enabled: true,
                perPage: pageLength,
                perPageDropdown: pageOptions,
                dropdownAllowAll: false,
                position: 'bottom',
              }"
            >
              <template slot="table-row" slot-scope="props">
                <span v-if="props.column.field === 'settlement_from'" class="text-nowrap">
                  {{ formatDateRange(props.row.settlement_from, props.row.settlement_to) }}
                </span>
                <span v-else-if="props.column.field === 'no_of_transaction'" class="font-weight-bolder">
                  {{ props.row.no_of_transaction || 0 }}
                </span>
                <span v-else-if="props.column.field === 'receivable_amount'" class="font-weight-bolder text-dark">
                  {{ formatAmount(props.row.receivable_amount) }}
                </span>
                <span v-else-if="props.column.field === 'bank_trans_id'" class="truncate" :title="props.row.bank_trans_id || '-'">
                  {{ props.row.bank_trans_id || "-" }}
                </span>
                <span v-else-if="props.column.field === 'status'">
                  <b-badge :variant="statusVariant(props.row.status)">
                    {{ statusLabel(props.row.status) }}
                  </b-badge>
                </span>
                <span v-else-if="props.column.field === 'actions'">
                  <router-link
                    v-if="props.row.id"
                    :to="{ name: 'admin-settlement-details', params: { settlementId: props.row.id } }"
                    target="_blank"
                    class="font-weight-bold"
                  >
                    <feather-icon icon="EyeIcon" size="14" class="mr-25" />
                    View
                  </router-link>
                  <span v-else>-</span>
                </span>
                <span v-else>
                  {{ props.formattedRow[props.column.field] }}
                </span>
              </template>
            </vue-good-table>
          </div>
        </div>

        <div
          v-if="!isLoading && !loadError && filteredRows.length === 0"
          class="text-center text-muted my-2"
        >
          No settlements found. Try adjusting filters.
        </div>
      </b-card-body>
    </b-card>
  </section>
</template>

<script>
import {
  BAlert,
  BAvatar,
  BBadge,
  BButton,
  BCard,
  BCardBody,
  BCol,
  BFormInput,
  BFormSelect,
  BFormGroup,
  BRow,
} from "bootstrap-vue";
import { VueGoodTable } from "vue-good-table";
import store from "@/store";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import { SETTLEMENT_LIST } from "@core/services/api";
import flatPickr from "vue-flatpickr-component";

export default {
  components: {
    BAlert,
    BAvatar,
    BBadge,
    BButton,
    BCard,
    BCardBody,
    BCol,
    BFormInput,
    BFormSelect,
    BFormGroup,
    BRow,
    VueGoodTable,
    ToastificationContent,
    flatPickr,
  },
  data() {
    return {
      ispUser: JSON.parse(localStorage.getItem("userData")) || {},
      settlements: [],
      searchTerm: "",
      statusFilter: "all",
      from_date: "",
      to_date: "",
      dateConfig: {
        dateFormat: "Y-m-d",
      },
      pageLength: 10,
      pageOptions: [10, 25, 50, 100],
      isLoading: false,
      loadError: "",
      presetOptions: [
        { key: "today", label: "Today" },
        { key: "last7", label: "Last 7 Days" },
        { key: "month", label: "This Month" },
        { key: "lastMonth", label: "Last Month" },
        { key: "last3", label: "Last 3 Months" },
        { key: "year", label: "This Year" },
        { key: "lastYear", label: "Last Year" },
      ],
      activePreset: null,
      suppressPresetUpdate: false,
      statusAutoRefreshReady: false,
    };
  },
  computed: {
    direction() {
      return store.state.appConfig.isRTL;
    },
    ispId() {
      return this.ispUser?.isp_id || this.ispUser?.id || null;
    },
    statusFilterOptions() {
      return [
        { value: "all", text: "All Status" },
        { value: "2", text: "Paid" },
        { value: "1", text: "Unpaid" },
        { value: "0", text: "Draft" },
      ];
    },
    statusFilterLabel() {
      const match = this.statusFilterOptions.find(
        (option) => String(option.value) === String(this.statusFilter)
      );
      return match ? match.text : "All Status";
    },
    columns() {
      return [
        { label: "Date", field: "settlement_from", sortable: true },
        { label: "Transactions", field: "no_of_transaction", sortable: true },
        { label: "Received (TK)", field: "receivable_amount", sortable: true },
        { label: "Bank Tx ID", field: "bank_trans_id", sortable: true },
        { label: "Status", field: "status", sortable: true },
        { label: "Action", field: "actions", sortable: false },
      ];
    },
    dateError() {
      if (!this.from_date || !this.to_date) return "";
      const from = this.parseDateValue(this.from_date);
      const to = this.parseDateValue(this.to_date);
      if (!from || !to) return "";
      if (from > to) return "From date cannot be after To date.";
      return "";
    },
    filteredRows() {
      const rows = Array.isArray(this.settlements) ? this.settlements : [];
      const term = this.searchTerm.trim().toLowerCase();
      const statusFiltered = this.statusFilter === "all"
        ? rows
        : rows.filter((row) => String(row.status) === String(this.statusFilter));

      const dateFiltered = this.applyDateFilter(statusFiltered);
      if (!term) {
        return dateFiltered;
      }
      return dateFiltered.filter((row) => {
        const haystack = [
          row.bank_trans_id,
          row.settlement_from,
          row.settlement_to,
          row.receivable_amount,
          row.no_of_transaction,
          this.statusLabel(row.status),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return haystack.includes(term);
      });
    },
    summaryCards() {
      const totalSettlements = this.settlements.length;
      const totalTransactions = this.settlements.reduce(
        (sum, item) => sum + Number(item.no_of_transaction || 0),
        0
      );
      const totalReceived = this.settlements.reduce(
        (sum, item) => sum + Number(item.receivable_amount || 0),
        0
      );
      const latestSettlement = this.latestSettlementDate();

      return [
        {
          label: "Total Settlements",
          value: totalSettlements,
          icon: "LayersIcon",
          color: "light-primary",
        },
        {
          label: "Total Transactions",
          value: totalTransactions,
          icon: "RepeatIcon",
          color: "light-info",
        },
        {
          label: "Total Received",
          value: this.formatAmount(totalReceived),
          icon: "DollarSignIcon",
          color: "light-success",
        },
        {
          label: "Last Settlement",
          value: latestSettlement || "-",
          icon: "CalendarIcon",
          color: "light-warning",
        },
      ];
    },
  },
  mounted() {
    this.settlementListData().finally(() => {
      this.statusAutoRefreshReady = true;
    });
  },
  watch: {
    from_date() {
      if (!this.suppressPresetUpdate) {
        this.activePreset = null;
      }
    },
    to_date() {
      if (!this.suppressPresetUpdate) {
        this.activePreset = null;
      }
    },
    statusFilter() {
      if (!this.statusAutoRefreshReady) return;
      this.settlementListData({ showToast: true });
    },
  },
  methods: {
    settlementListData(options = {}) {
      const { showToast = false } = options;
      if (!this.ispId) {
        this.loadError = "Unable to detect ISP.";
        this.settlements = [];
        return Promise.resolve();
      }
      this.isLoading = true;
      this.loadError = "";
      return this.$http
        .get(process.env.VUE_APP_BASEURL + SETTLEMENT_LIST, {
          params: {
            isp_id: this.ispId,
          },
        })
        .then((res) => {
          const payload = res.data && res.data.data ? res.data.data : {};
          this.settlements = payload.settlements || [];
          if (showToast) {
            this.notifyInfo(`Status filter: ${this.statusFilterLabel}`);
          }
        })
        .catch(() => {
          this.loadError = "Failed to load settlements. Please try again.";
          this.settlements = [];
          this.notifyError(this.loadError);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    resetFilters() {
      this.statusFilter = "all";
      this.searchTerm = "";
      this.from_date = "";
      this.to_date = "";
      this.activePreset = null;
    },
    applyPreset(key) {
      const today = new Date();
      this.suppressPresetUpdate = true;
      this.activePreset = key;
      if (key === "today") {
        this.from_date = this.formatDate(today);
        this.to_date = this.formatDate(today);
        this.suppressPresetUpdate = false;
        return;
      }
      if (key === "last7") {
        const start = new Date();
        start.setDate(today.getDate() - 6);
        this.from_date = this.formatDate(start);
        this.to_date = this.formatDate(today);
        this.suppressPresetUpdate = false;
        return;
      }
      if (key === "month") {
        const start = new Date(today.getFullYear(), today.getMonth(), 1);
        this.from_date = this.formatDate(start);
        this.to_date = this.formatDate(today);
        this.suppressPresetUpdate = false;
        return;
      }
      if (key === "lastMonth") {
        const start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const end = new Date(today.getFullYear(), today.getMonth(), 0);
        this.from_date = this.formatDate(start);
        this.to_date = this.formatDate(end);
        this.suppressPresetUpdate = false;
        return;
      }
      if (key === "last3") {
        const start = new Date(today.getFullYear(), today.getMonth() - 2, 1);
        this.from_date = this.formatDate(start);
        this.to_date = this.formatDate(today);
        this.suppressPresetUpdate = false;
        return;
      }
      if (key === "year") {
        const start = new Date(today.getFullYear(), 0, 1);
        this.from_date = this.formatDate(start);
        this.to_date = this.formatDate(today);
        this.suppressPresetUpdate = false;
        return;
      }
      if (key === "lastYear") {
        const start = new Date(today.getFullYear() - 1, 0, 1);
        const end = new Date(today.getFullYear() - 1, 11, 31);
        this.from_date = this.formatDate(start);
        this.to_date = this.formatDate(end);
      }
      this.suppressPresetUpdate = false;
    },
    statusLabel(status) {
      const code = Number(status);
      if (code === 2) return "Paid";
      if (code === 1) return "Unpaid";
      return "Draft";
    },
    statusVariant(status) {
      const code = Number(status);
      if (code === 2) return "light-success";
      if (code === 1) return "light-danger";
      return "light-secondary";
    },
    formatAmount(value) {
      const amount = Number(value);
      if (Number.isNaN(amount)) return value || "0 TK";
      const formatted = amount % 1 === 0
        ? String(Math.trunc(amount))
        : amount.toFixed(2).replace(/\.?0+$/, "");
      return `${formatted} TK`;
    },
    formatDateRange(fromDate, toDate) {
      if (!fromDate && !toDate) return "-";
      if (!toDate || fromDate === toDate) return fromDate || toDate;
      return `${fromDate} - ${toDate}`;
    },
    formatDate(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },
    parseDateValue(value) {
      if (!value) return null;
      if (value instanceof Date) return value;
      const parsed = new Date(value);
      if (Number.isNaN(parsed.getTime())) return null;
      return parsed;
    },
    applyDateFilter(rows) {
      if (this.dateError) return rows;
      const from = this.parseDateValue(this.from_date);
      const to = this.parseDateValue(this.to_date);
      if (!from && !to) return rows;
      return rows.filter((row) => {
        const rowDate = this.parseDateValue(row.settlement_from || row.settlement_to);
        if (!rowDate) return false;
        if (from && rowDate < from) return false;
        if (to && rowDate > to) return false;
        return true;
      });
    },
    latestSettlementDate() {
      const dates = this.settlements
        .map((item) => item.settlement_to || item.settlement_from)
        .filter(Boolean)
        .sort();
      return dates.length ? dates[dates.length - 1] : "-";
    },
    notifyError(message) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: "Settlement List",
          icon: "AlertTriangleIcon",
          variant: "danger",
          text: message,
        },
      });
    },
    notifyInfo(message) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: "Settlement List",
          icon: "InfoIcon",
          variant: "primary",
          text: message,
        },
      });
    },
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-good-table.scss";
@import "@core/scss/vue/libs/vue-flatpicker.scss";

.settlement-card {
  .settlement-kpi-card {
    background: #f8fafc;
    border: 1px solid rgba(0, 0, 0, 0.06);
    border-radius: 0.6rem;
    padding: 0.5rem 0.75rem;
    min-height: 58px;
  }

  .settlement-filter-panel {
    background: #fbfbfc;
    border: 1px solid #e6e8eb;
    border-radius: 0.6rem;
    padding: 0.5rem 0.75rem;
  }
}

.settlement-actions .form-control,
.settlement-actions .custom-select {
  height: 36px;
  font-size: 0.85rem;
}

.settlement-search {
  width: 240px;
}

@media (min-width: 992px) {
  .settlement-actions {
    flex-wrap: nowrap;
  }
}

@media (max-width: 576px) {
  .settlement-search,
  .settlement-status {
    width: 100%;
    flex: 1 0 100%;
  }
}

.settlement-skeleton {
  display: grid;
  gap: 0.75rem;
}

.settlement-skeleton-row {
  display: grid;
  grid-template-columns: minmax(140px, 1.2fr) minmax(120px, 0.8fr) minmax(140px, 1fr) minmax(140px, 1fr) minmax(80px, 0.6fr);
  gap: 0.75rem;
}

.settlement-skeleton-block {
  height: 12px;
  border-radius: 999px;
  background: linear-gradient(90deg, #eceff3 0%, #f7f8fa 50%, #eceff3 100%);
  background-size: 200% 100%;
  animation: settlement-skeleton 1.4s ease-in-out infinite;
}

.settlement-skeleton-block--lg {
  height: 14px;
}

.settlement-skeleton-block--sm {
  max-width: 120px;
}

.quick-range-row {
  gap: 0.75rem;
  flex-wrap: nowrap;
}
.quick-range-list {
  gap: 0.5rem;
  flex-wrap: nowrap;
  overflow-x: auto;
  white-space: nowrap;
}
.quick-range-list::-webkit-scrollbar {
  height: 4px;
}
.quick-range-list::-webkit-scrollbar-thumb {
  background: #d7dbe3;
  border-radius: 999px;
}

.settlement-preset {
  border: 1px solid #d7dbe3;
  background: #f3f6fb;
  color: #3b4252;
  padding: 0.15rem 0.5rem;
  font-size: 0.75rem;
  transition: all 0.2s ease;
}
.settlement-preset:hover {
  background: #e8efff;
}
.settlement-preset--active {
  background: #2f5bff;
  border-color: #2f5bff;
  color: #fff;
  box-shadow: 0 6px 16px rgba(47, 91, 255, 0.24);
}

.settlement-card-item {
  border: 1px solid #e5e7eb;
  border-radius: 0.75rem;
  padding: 0.75rem;
  background: #fff;
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.04);
}

.settlement-table th {
  background: #f8fafc;
  font-weight: 600;
  color: #4b5563;
  border-bottom: 1px solid #e5e7eb;
  position: sticky;
  top: 0;
  z-index: 2;
}

.settlement-table th,
.settlement-table td {
  padding: 0.55rem 0.75rem;
  vertical-align: middle;
  white-space: nowrap;
}

.settlement-table .truncate {
  max-width: 160px;
  display: inline-block;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: bottom;
}

.settlement-table tr:hover {
  background: #f9fafb;
}

@keyframes settlement-skeleton {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}
</style>
